import type {
  Branch,
  Department,
  Employee,
  UserAttendance,
} from "@/services/api/organizationalUnitService";
import OrganizationalUnitService from "@/services/api/organizationalUnitService";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const name = "organizationalUnit";

interface LoadingState {
    department:boolean;
    designation:boolean;
    branch:boolean;
}

interface RoleState {
  isFetching: boolean;
  error: string | null;
  department: { Items?: Department[]; TotalRecords?: number };
  designation: { Items?: Employee[]; TotalRecords?: number };
  departmentHead: { Members?: UserAttendance[]; TotalRecords?: number };
  branch: { Items?: Branch[]; TotalRecords?: number };
  loading: LoadingState;
}


const initialState: RoleState = {
  isFetching: false,
  error: null,
  department: {
    Items: [],
    TotalRecords: 0,
  },
  departmentHead: {
    Members: [],
    TotalRecords: 0,
  },
  designation: {
    Items: [],
    TotalRecords: 0,
  },
  branch: {
    Items: [],
    TotalRecords: 0,
  },
  loading: {
    department: false,
    designation: false,
    branch: false,
  },
};

export const getDepartments = createAsyncThunk(
  `${name}/getDepartments`,
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await OrganizationalUnitService.getDepartment(params);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.Message || "Failed to fetch departments");
    }
  },
);

export const getDepartmentHeads = createAsyncThunk(
  `${name}/getDepartmentHeads`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await OrganizationalUnitService.getDepartmentHeads(payload);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || "Failed to fetch department heads",
      );
    }
  },
);

//Designation thunks
export const getDesignations = createAsyncThunk(
  `${name}/getDesignations`,
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await OrganizationalUnitService.getDesignations(params);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.Message || "Failed to fetch designations");
    }
  },
);

//Branch thunks
export const getBranches = createAsyncThunk(
  `${name}/getBranches`,
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await OrganizationalUnitService.getBranch(params);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.Message || "Failed to fetch branches");
    }
  },
);

const organizationalUnitSlice = createSlice({
  name: name,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDepartments.pending, (state, action) => {
        state.loading.department  = true;
      })
      .addCase(getDepartments.fulfilled, (state, action) => {
        state.loading.department  = false;
        state.department = action.payload;
      })
      .addCase(getDepartments.rejected, (state, action) => {
        state.loading.department  = false;
        state.error = action.payload as string;
      })
      .addCase(getDepartmentHeads.fulfilled, (state, action) => {
        state.isFetching = false;
        state.departmentHead = action.payload;
      })
      .addCase(getDepartmentHeads.pending, (state, action) => {
        state.isFetching = true;
      })
      .addCase(getDepartmentHeads.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      .addCase(getDesignations.pending, (state, action) => {
        state.loading.designation = true;
        state.error = null;
      })
      .addCase(getDesignations.fulfilled, (state, action) => {
        state.loading.designation = false;
        state.designation = action.payload;
      })
      .addCase(getDesignations.rejected, (state, action) => {
        state.loading.designation = false;
        state.error = action.payload as string;
      })
      .addCase(getBranches.pending, (state, action) => {
        state.loading.branch = true;
        state.error = null;
      })
      .addCase(getBranches.fulfilled, (state, action) => {
        state.loading.branch = false;
        state.branch = action.payload;
      })
      .addCase(getBranches.rejected, (state, action) => {
        state.loading.branch = false;
        state.error = action.payload as string;
      });
  },
});

export default organizationalUnitSlice.reducer;
